import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import { getImagePath } from "../Actions/FetchContent";
import api from "../utils/axiosInstance";
import {
  AdminShell, FilterBar, SelectField, DeleteModal,
  SkeletonRows, EmptyState, PagePreview, CountBadge,
  IconEdit, IconDelete, IconPlus, IconLink,
} from './AdminUI';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const config = { headers: { 'Content-Type': 'application/json' }, withCredentials: true };

const AllLogoSlider = () => {
  const location        = useLocation();
  const selectedPage    = location.state?.page    || '';
  const selectedSection = location.state?.section || '';
  const { user }        = useSelector(state => state.user);
  const alert           = useAlert();

  const [loading, setLoading]             = useState(true);
  const [content, setContent]             = useState([]);
  const [pages, setPages]                 = useState([]);
  const [sections, setSections]           = useState([]);
  const [pageid, setPageId]               = useState(selectedPage);
  const [selectedSectionId, setSelectedSectionId] = useState(selectedSection);
  const [pageUrl, setPageUrl]             = useState('');
  const [showPreview, setShowPreview]     = useState(false);
  const [deleteTarget, setDeleteTarget]   = useState(null);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${API_BASE_URL}/api/v1/admin/all/pages/${user.userid}/logo_slider`, config); setPages(r.data.data || []); }
    catch { alert.error('Failed to load pages.'); } finally { setLoading(false); }
  }, [user.userid]);

  const fetchSections = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE_URL}/api/v1/faq/sections/${id}`, config);
      const f = (r.data.data || []).filter(s => s.section_type === 'logo_slider');
      setSections(f);
      if (f.length && !selectedSectionId) setSelectedSectionId(f[0].sec_id);
      if (!f.length) setContent([]);
    } catch { alert.error('Failed to fetch sections.'); setSections([]); } finally { setLoading(false); }
  }, [selectedSectionId]);

  const fetchContent = useCallback(async (secid) => {
    if (!secid) return;
    setLoading(true);
    try { const r = await api.get(`${API_BASE_URL}/api/v1/faq/content/${secid}`); setContent(r.data.data || []); }
    catch { alert.error('Failed to fetch content.'); setContent([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPages(); }, []);
  useEffect(() => { if (selectedPage) { setPageId(selectedPage); fetchSections(selectedPage); } }, [selectedPage]);
  useEffect(() => { if (selectedSectionId) fetchContent(selectedSectionId); }, [selectedSectionId]);
  useEffect(() => { if (pages.length && !selectedPage) { const f = pages[0].pageid; setPageId(f); fetchSections(f); } }, [pages]);

  const handlePageChange = async (e) => {
    const id = e.target.value;
    setPageUrl(pages.find(p => p.pageid === parseInt(id))?.page_url || '');
    setPageId(id); setSelectedSectionId(''); setContent([]); setShowPreview(false);
    await fetchSections(id);
  };
  const handleSectionChange = async (e) => { setSelectedSectionId(e.target.value); await fetchContent(e.target.value); };
  const confirmDelete = async () => {
    try { await api.delete(`${API_BASE_URL}/api/v1/faq/content/${deleteTarget.entry_id}`, config); alert.success('Deleted.'); setContent(p => p.filter(f => f.entry_id !== deleteTarget.entry_id)); }
    catch { alert.error('Failed to delete.'); } finally { setDeleteTarget(null); }
  };

  const pageName    = pages.find(p => p.pageid === parseInt(pageid))?.page_name;
  const sectionName = sections.find(s => s.sec_id === parseInt(selectedSectionId))?.section_name;

  return (
    <>
      <AdminShell
        title="Logo Slider"
        subtitle={pageName ? `${pageName}${sectionName ? ` › ${sectionName}` : ''}` : 'Select a page to begin'}
        actions={pageid && selectedSectionId
          ? <Link to={`/admin/faq/content/${pageid}/${selectedSectionId}/logo_slider`} className="admin-btn admin-btn-primary"><IconPlus /> Add Logo</Link>
          : null}
      >
        <FilterBar extra={<PagePreview url={pageUrl} show={showPreview} onToggle={() => setShowPreview(v => !v)} />}>
          <SelectField label="Page" value={pageid} onChange={handlePageChange}>
            <option value="">Select a page…</option>
            {pages.map(p => <option key={p.pageid} value={p.pageid}>{p.page_name}</option>)}
          </SelectField>
          <SelectField label="Section" value={selectedSectionId || ''} onChange={handleSectionChange} disabled={!sections.length}>
            <option value="">{sections.length ? 'Select a section…' : 'No sections'}</option>
            {sections.map(s => <option key={s.sec_id} value={s.sec_id}>{s.section_name}</option>)}
          </SelectField>
        </FilterBar>

        {content.length > 0 && !loading && <div style={{ marginBottom:12 }}><CountBadge count={content.length} label="logos" /></div>}

        {/* Logo grid */}
        {loading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:14 }}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{ background:'#fff', borderRadius:12, overflow:'hidden', border:'1px solid #f3f4f6' }}>
                <div className="sk" style={{ height:120 }} />
                <div style={{ padding:12 }}><div className="sk" style={{ height:12, width:'60%' }} /></div>
              </div>
            ))}
          </div>
        ) : content.length > 0 ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:14 }}>
            {content.map(item => (
              <div key={item.entry_id} style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden', transition:'box-shadow .2s,transform .2s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'; }}
              >
                {/* Logo image */}
                <div style={{ height:120, background:'#f9fafb', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
                  {getImagePath(item.image_url)
                    ? <img src={getImagePath(item.image_url)} alt="logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                    : <span style={{ fontSize:12, color:'#d1d5db' }}>No image</span>
                  }
                </div>

                {/* Footer */}
                <div style={{ padding:'8px 10px', borderTop:'1px solid #f3f4f6' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:'#9ca3af', fontWeight:600 }}>#{item.sequence}</span>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" title={item.link} style={{ color:'#2563eb', display:'flex' }}><IconLink /></a>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:4, marginTop:6 }}>
                    <Link to={`/admin/update/faq/content/${item.entry_id}/${item.pageid}/${item.secid}`}
                      style={{ flex:1, padding:'5px 0', textAlign:'center', borderRadius:6, fontSize:11, fontWeight:600, color:'#2563eb', background:'#eff6ff', textDecoration:'none' }}>
                      Edit
                    </Link>
                    <button onClick={() => setDeleteTarget(item)}
                      style={{ flex:1, padding:'5px 0', border:'none', borderRadius:6, fontSize:11, fontWeight:600, color:'#ef4444', background:'#fef2f2', cursor:'pointer' }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', padding:'52px 20px', textAlign:'center' }}>
            <p style={{ fontSize:15, fontWeight:700, color:'#374151', margin:'0 0 6px' }}>No logos yet</p>
            <p style={{ fontSize:13, color:'#9ca3af', margin:'0 0 20px' }}>{pageid && selectedSectionId ? 'Upload the first logo.' : 'Select a page and section first.'}</p>
            {pageid && selectedSectionId && <Link to={`/admin/faq/content/${pageid}/${selectedSectionId}/logo_slider`} className="admin-btn admin-btn-primary" style={{ display:'inline-flex' }}><IconPlus /> Add Logo</Link>}
          </div>
        )}

        {showPreview && pageUrl && <div style={{ marginTop:24 }}><PagePreview url={pageUrl} show={showPreview} onToggle={() => setShowPreview(false)} /></div>}
      </AdminShell>

      {deleteTarget && <DeleteModal message="This logo will be permanently deleted." onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} />}
    </>
  );
};

export default AllLogoSlider;